import React, { useEffect, useState } from "react";
import { Link, Outlet, NavLink } from "react-router-dom";
import { Users, BarChart3, ArrowLeft, LogOut, ShieldCheck, CalendarX, FileText, Search, Star, Receipt, Package, Calculator, Ticket, Image as ImageIcon, CalendarCheck, Inbox, Bell, BookOpen, ClipboardCheck, Contact, Calendar, CreditCard, LayoutDashboard, Boxes, Gamepad2 , CalendarDays, Layers } from "lucide-react";
import { AdminGlobalSearch } from "@/pages/admin/AdminGlobalSearch";
import { useAuth } from "@/contexts/AuthContext";
import { adminApi } from "@/lib/api";

// Sidebar organisée en groupes thématiques (Sprint 3)
// Les composants MyAdmin* sont montés sous /admin/* — pas de duplication.
const NAV_SECTIONS = [
  {
    id: "overview",
    title: "Vue d'ensemble",
    items: [
      { to: "/admin", label: "Vue d'ensemble", icon: BarChart3, end: true },
      { to: "/admin/dashboard-next", label: "Dashboard NEXT", icon: LayoutDashboard },
    ],
  },
  {
    id: "clients",
    title: "Clients",
    items: [
      { to: "/admin/clients", label: "Clients", icon: Users },
    ],
  },
  {
    id: "myevent",
    title: "MyEvent",
    items: [
      { to: "/admin/events", label: "Événements", icon: Calendar },
      { to: "/admin/plans", label: "Forfaits & Tarifs", icon: Package },
      { to: "/admin/payments", label: "Paiements", icon: CreditCard },
    ],
  },
  {
    id: "commercial",
    title: "Commercial",
    items: [
      { to: "/admin/devis", label: "Devis & Factures", icon: Receipt },
      { to: "/admin/demandes-devis", label: "Demandes de devis", icon: Inbox },
      { to: "/admin/catalogue", label: "Catalogue produits", icon: Package },
      { to: "/admin/billetterie", label: "Billetterie", icon: Ticket },
    ],
  },
  {
    id: "prestations",
    title: "Prestations",
    items: [
      { to: "/admin/photobooth", label: "Galeries photobooth", icon: ImageIcon },
      { to: "/admin/prestataires", label: "Prestataires", icon: Contact },
      { to: "/admin/validations", label: "Templates & maquettes", icon: ClipboardCheck },
    ],
  },
  {
    id: "organisation",
    title: "Organisation",
    items: [
      { to: "/admin/rdv", label: "Agenda RDV", icon: CalendarCheck },
      { to: "/admin/calendrier", label: "Calendrier", icon: CalendarX },
    ],
  },
  {
    id: "compta",
    title: "Comptabilité",
    items: [
      { to: "/admin/comptabilite", label: "Comptabilité", icon: Calculator },
      { to: "/admin/livre-recettes", label: "Livre de recettes", icon: BookOpen },
    ],
  },
  {
    id: "location",
    title: "Location",
    items: [
      { to: "/admin/location", label: "Tableau de bord location", icon: Boxes, end: true },
      { to: "/admin/location/calendrier", label: "Calendrier location", icon: CalendarDays },
      { to: "/admin/location/locations", label: "Locations", icon: Package },
      { to: "/admin/location/modeles", label: "Modèles de location", icon: Layers },
      { to: "/admin/location/demandes", label: "Demandes", icon: Inbox },
      { to: "/admin/location/catalogue", label: "Catalogue location", icon: Package },
      { to: "/admin/location/cautions", label: "Cautions", icon: CreditCard },
      { to: "/admin/location/parametres", label: "Paramètres location", icon: ClipboardCheck },
    ],
  },
  {
    id: "jeux",
    title: "Jeux & Animations",
    items: [{ to: "/admin/jeux", label: "Jeux & Animations", icon: Gamepad2 }],
  },
  {
    id: "communication",
    title: "Communication",
    items: [
      { to: "/admin/blog", label: "Blog", icon: FileText },
      { to: "/admin/avis", label: "Avis clients", icon: Star },
      { to: "/admin/notifications", label: "Notifications push", icon: Bell },
      { to: "/admin/seo", label: "SEO & Search Console", icon: Search },
    ],
  },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [pendingReviews, setPendingReviews] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = () => {
      adminApi.pendingReviewsCount()
        .then((r) => { if (!cancelled) setPendingReviews(r?.pending || 0); })
        .catch(() => {});
    };
    fetchCount();
    // Refresh every 90 seconds — non-intrusive
    const id = setInterval(fetchCount, 90 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-[#DCB457]/15 bg-black/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-serif-luxe text-xl md:text-2xl gold-text">My Animation</Link>
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-1">
              <ShieldCheck size={12} strokeWidth={1.5} /> Admin
            </span>
          </div>
          <AdminGlobalSearch />
          <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.22em] text-white/55">
            <span className="hidden md:inline">{user?.email}</span>
            <Link to="/espace-client" className="hover:text-[#DCB457] inline-flex items-center gap-1.5"><ArrowLeft size={13} strokeWidth={1.4} /> Espace client</Link>
            <button onClick={logout} data-testid="admin-logout" className="hover:text-[#DCB457] inline-flex items-center gap-1.5"><LogOut size={13} strokeWidth={1.4} /> Déconnexion</button>
          </div>
        </div>
      </header>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <nav className="card-luxe overflow-hidden" data-testid="admin-sidebar">
            {NAV_SECTIONS.map((section, sIdx) => (
              <div key={section.id} data-testid={`admin-nav-section-${section.id}`}>
                <p
                  className={`px-5 pt-4 pb-2 text-[10px] uppercase tracking-[0.24em] text-[#DCB457]/80 ${sIdx > 0 ? "border-t border-[#DCB457]/15 mt-1" : ""}`}
                >
                  {section.title}
                </p>
                {section.items.map(({ to, label, icon: Icon, end }) => {
                  const isReviews = to === "/admin/avis";
                  const badgeCount = isReviews ? pendingReviews : 0;
                  return (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    data-testid={`admin-nav-${label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-5 py-4 text-sm border-b border-[#DCB457]/10 last:border-b-0 ${
                        isActive ? "bg-[#DCB457]/10 text-[#DCB457] border-l-2 border-l-[#DCB457]" : "text-white/65 hover:text-[#DCB457] hover:bg-[#DCB457]/5"
                      }`
                    }
                  >
                    <Icon size={16} strokeWidth={1.3} />
                    <span className="flex-1">{label}</span>
                    {badgeCount > 0 && (
                      <span
                        data-testid="admin-nav-avis-badge"
                        className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 text-[10px] font-semibold text-black bg-[#DCB457] rounded-full leading-none"
                        title={`${badgeCount} avis en attente de modération`}
                      >
                        {badgeCount}
                      </span>
                    )}
                  </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>
        </aside>
        <section className="lg:col-span-9 min-w-0">
          <Outlet />
        </section>
      </div>
    </div>
  );
}
